import React, { useState, useEffect } from 'react';

interface ApiConfig {
  douyinWtf: {
    enabled: boolean;
    apiKey: string;
    apiUrl: string;
  };
  customApi1: {
    enabled: boolean;
    apiKey: string;
    apiUrl: string;
  };
  customApi2: {
    enabled: boolean;
    apiKey: string;
    apiUrl: string;
  };
}

const ApiSettings: React.FC = () => {
  const [config, setConfig] = useState<ApiConfig>({
    douyinWtf: {
      enabled: false,
      apiKey: '',
      apiUrl: 'https://api.douyin.wtf/api'
    },
    customApi1: {
      enabled: false,
      apiKey: '',
      apiUrl: ''
    },
    customApi2: {
      enabled: false,
      apiKey: '',
      apiUrl: ''
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // 加载配置
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/config');
      if (response.ok) {
        const data = await response.json();
        setConfig(data.douyinApis || {
          douyinWtf: { enabled: false, apiKey: '', apiUrl: 'https://api.douyin.wtf/api' },
          customApi1: { enabled: false, apiKey: '', apiUrl: '' },
          customApi2: { enabled: false, apiKey: '', apiUrl: '' }
        });
      } else {
        setError('加载配置失败');
      }
    } catch (err) {
      setError('网络错误');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      const response = await fetch('/api/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ douyinApis: config })
      });
      
      if (response.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || '保存配置失败');
      }
    } catch (err) {
      setError('保存过程中发生错误');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (api: keyof ApiConfig, field: string, value: string | boolean) => {
    setConfig(prev => ({
      ...prev,
      [api]: {
        ...prev[api],
        [field]: value
      }
    }));
  };

  const testApi = async (apiName: keyof ApiConfig) => {
    try {
      const testUrl = 'https://v.douyin.com/DE_7CokPYXI/';
      const response = await fetch(`/api/test-api/${apiName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url: testUrl, config: config[apiName] })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          alert(`✅ ${apiName} API 测试成功！`);
        } else {
          alert(`❌ ${apiName} API 测试失败：${result.message}`);
        }
      } else {
        alert(`❌ ${apiName} API 测试失败：服务器错误`);
      }
    } catch (err) {
      alert(`❌ ${apiName} API 测试失败：网络错误`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-lg">加载配置中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">⚙️ 第三方API设置</h1>
        
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <div className="mb-4 p-4 bg-blue-900/30 border border-blue-700/50 rounded-lg">
            <p className="text-blue-300 text-sm">
              📝 <strong>配置说明</strong>：在此页面配置您申请的第三方抖音解析API。
              启用API后，系统将优先使用这些API进行视频解析，成功率更高且能获取真实无水印视频。
            </p>
          </div>
          
          {/* douyin.wtf API */}
          <div className="mb-8 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-xl font-semibold mb-4 text-green-400">douyin.wtf API</h3>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="douyinWtfEnabled"
                  checked={config.douyinWtf.enabled}
                  onChange={(e) => handleInputChange('douyinWtf', 'enabled', e.target.checked)}
                  className="w-5 h-5"
                />
                <label htmlFor="douyinWtfEnabled" className="text-white">启用 douyin.wtf API</label>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">API密钥 (Token)</label>
                <input
                  type="password"
                  value={config.douyinWtf.apiKey}
                  onChange={(e) => handleInputChange('douyinWtf', 'apiKey', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white"
                  placeholder="输入您的API密钥"
                  disabled={!config.douyinWtf.enabled}
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">API地址</label>
                <input
                  type="text"
                  value={config.douyinWtf.apiUrl}
                  onChange={(e) => handleInputChange('douyinWtf', 'apiUrl', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white"
                  placeholder="https://api.douyin.wtf/api"
                  disabled={!config.douyinWtf.enabled}
                />
              </div>
              
              <button
                onClick={() => testApi('douyinWtf')}
                disabled={!config.douyinWtf.enabled || !config.douyinWtf.apiKey}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                测试API连接
              </button>
            </div>
          </div>
          
          {/* 自定义API 1 */}
          <div className="mb-8 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-xl font-semibold mb-4 text-yellow-400">自定义API 1</h3>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="customApi1Enabled"
                  checked={config.customApi1.enabled}
                  onChange={(e) => handleInputChange('customApi1', 'enabled', e.target.checked)}
                  className="w-5 h-5"
                />
                <label htmlFor="customApi1Enabled" className="text-white">启用自定义API 1</label>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">API密钥</label>
                <input
                  type="password"
                  value={config.customApi1.apiKey}
                  onChange={(e) => handleInputChange('customApi1', 'apiKey', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white"
                  placeholder="输入API密钥（可选）"
                  disabled={!config.customApi1.enabled}
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">API地址</label>
                <input
                  type="text"
                  value={config.customApi1.apiUrl}
                  onChange={(e) => handleInputChange('customApi1', 'apiUrl', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white"
                  placeholder="例如：https://your-api.com/parse"
                  disabled={!config.customApi1.enabled}
                />
              </div>
              
              <button
                onClick={() => testApi('customApi1')}
                disabled={!config.customApi1.enabled || !config.customApi1.apiUrl}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                测试API连接
              </button>
            </div>
          </div>
          
          {/* 自定义API 2 */}
          <div className="mb-8 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-xl font-semibold mb-4 text-purple-400">自定义API 2</h3>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="customApi2Enabled"
                  checked={config.customApi2.enabled}
                  onChange={(e) => handleInputChange('customApi2', 'enabled', e.target.checked)}
                  className="w-5 h-5"
                />
                <label htmlFor="customApi2Enabled" className="text-white">启用自定义API 2</label>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">API密钥</label>
                <input
                  type="password"
                  value={config.customApi2.apiKey}
                  onChange={(e) => handleInputChange('customApi2', 'apiKey', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white"
                  placeholder="输入API密钥（可选）"
                  disabled={!config.customApi2.enabled}
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">API地址</label>
                <input
                  type="text"
                  value={config.customApi2.apiUrl}
                  onChange={(e) => handleInputChange('customApi2', 'apiUrl', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white"
                  placeholder="例如：https://your-api.com/parse"
                  disabled={!config.customApi2.enabled}
                />
              </div>
              
              <button
                onClick={() => testApi('customApi2')}
                disabled={!config.customApi2.enabled || !config.customApi2.apiUrl}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                测试API连接
              </button>
            </div>
          </div>
          
          {/* 保存按钮 */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={loadConfig}
              className="px-6 py-3 bg-gray-600 hover:bg-gray-700 rounded font-medium"
            >
              重新加载
            </button>
            <button
              onClick={saveConfig}
              disabled={saving}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded font-medium disabled:opacity-50"
            >
              {saving ? '保存中...' : '保存配置'}
            </button>
          </div>
          
          {error && (
            <div className="mt-4 p-3 bg-red-900 text-red-200 rounded">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mt-4 p-3 bg-green-900 text-green-200 rounded">
              配置保存成功！
            </div>
          )}
        </div>
        
        {/* 返回链接 */}
        <div className="text-center">
          <a 
            href="/admin" 
            className="inline-block bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            ← 返回管理后台
          </a>
        </div>
      </div>
    </div>
  );
};

export default ApiSettings;